/**
 * i18n Namespace Loader
 *
 * Loads language namespaces on-demand with caching and deduplication.
 * Does NOT know about routes - receives explicit namespace lists.
 *
 * @module i18n/namespaceLoader
 */

import { getNamespaceFallback } from './namespaces'

const FALLBACK_LANG = 'es'

/**
 * Cache for loaded namespaces
 * Format: 'es:core' → contents of core.json (e.g., { core: {...}, meta: {...}, home: {...} })
 */
const cache = new Map()

/**
 * In-flight requests to prevent duplicate fetches
 * Format: 'es:core' → Promise<Object>
 */

const inFlight = new Map()

/**
 * Vite glob import for all namespace files
 * This creates a map of all available JSON files in lang/ directory
 */
const namespaceModules = import.meta.glob('./lang/*/*.json', { eager: false })

/**
 * Load a single namespace for a language
 * Handles per-namespace fallbacks (e.g., legal falls back to es/en)
 *
 * @param {string} lang - Language code (es, en, it, etc.)
 * @param {string} namespace - Namespace name (core, shared, memorama, etc.)
 * @returns {Promise<Object>} Namespace translations object
 * @private
 */
async function loadNamespace (lang, namespace) {
  const key = `${lang}:${namespace}`
  const path = `./lang/${lang}/${namespace}.json`

  // Check if request is already in flight (dedupe)
  if (inFlight.has(key)) {
    return inFlight.get(key)
  }

  // Check if file exists for this language
  if (!namespaceModules[path]) {
    // Try namespace-specific fallbacks
    const fallbackLang = getNamespaceFallback(namespace, lang)

    if (fallbackLang) {
      const fallbackPath = `./lang/${fallbackLang}/${namespace}.json`

      if (namespaceModules[fallbackPath]) {
        if (import.meta.env.MODE === 'development') {
          console.info(`[i18n] Using ${fallbackLang}/${namespace}.json as fallback for ${lang}`)
        }

        // Load from fallback
        const promise = (async () => {
          try {
            const module = await namespaceModules[fallbackPath]()
            return module.default
          } catch (error) {
            console.error(`[i18n] Failed to load fallback ${fallbackLang}/${namespace}:`, error)
            return {}
          }
        })()

        inFlight.set(key, promise)

        try {
          const result = await promise
          return result
        } finally {
          inFlight.delete(key)
        }
      }
    }

    // No file and no fallback
    if (import.meta.env.MODE === 'development') {
      console.warn(`[i18n] ⚠️ Namespace "${namespace}" not found for "${lang}" - file missing: ${path}`)
    }
    return {}
  }

  // Create and store the promise for the original request
  const promise = (async () => {
    try {
      const module = await namespaceModules[path]()
      return module.default
    } catch (error) {
      console.error(`[i18n] Failed to load ${lang}/${namespace}:`, error)
      return {}
    }
  })()

  inFlight.set(key, promise)

  try {
    return await promise
  } finally {
    // Clean up after resolution (success or error)
    inFlight.delete(key)
  }
}

/**
 * Ensure namespaces are loaded for a language
 *
 * Checks cache first, loads missing namespaces in parallel,
 * and returns combined translations object.
 *
 * @param {string} lang - Language code (es, en, it, etc.)
 * @param {string[]} namespaces - Array of namespace names to load
 * @returns {Promise<Object>} Combined translations object
 *
 * @example
 * const t = await ensureNamespaces('es', ['core', 'shared', 'memorama'])
 * // Returns: { core: {...}, meta: {...}, home: {...}, shared: {...}, memorama: {...} }
 */
export async function ensureNamespaces (lang, namespaces) {
  const results = {}
  const toLoad = []

  // Check cache first
  for (const ns of namespaces) {
    const cacheKey = `${lang}:${ns}`
    if (cache.has(cacheKey)) {
      Object.assign(results, cache.get(cacheKey))
    } else {
      toLoad.push(ns)
    }
  }

  // Load missing namespaces in parallel
  if (toLoad.length > 0) {
    const loadPromises = toLoad.map(async (ns) => {
      const data = await loadNamespace(lang, ns)
      return { ns, data }
    })

    const loaded = await Promise.all(loadPromises)

    // Cache and merge results
    for (const { ns, data } of loaded) {
      const cacheKey = `${lang}:${ns}`
      // Only cache if we got data
      if (Object.keys(data).length > 0) {
        cache.set(cacheKey, data)
      }
      Object.assign(results, data)
    }
  }

  return results
}

/**
 * Load all required namespaces for a language with fallback
 *
 * This is the main entry point for LanguageProvider.
 * If loading fails, attempts to load Spanish as fallback.
 *
 * @param {string} lang - Language code
 * @param {string[]} namespaces - Namespaces to load
 * @returns {Promise<Object>} Translations object
 */
export async function loadNamespacesWithFallback (lang, namespaces) {
  try {
    const data = await ensureNamespaces(lang, namespaces)

    // Check if we got any data
    if (Object.keys(data).length === 0 && lang !== FALLBACK_LANG) {
      console.warn(`[i18n] No data for ${lang}, falling back to ${FALLBACK_LANG}`)
      return ensureNamespaces(FALLBACK_LANG, namespaces)
    }

    return data
  } catch (error) {
    console.error(`[i18n] Error loading ${lang}, falling back to ${FALLBACK_LANG}:`, error)

    if (lang !== FALLBACK_LANG) {
      return ensureNamespaces(FALLBACK_LANG, namespaces)
    }

    throw error
  }
}

/**
 * Check if a namespace is already cached
 *
 * @param {string} lang - Language code
 * @param {string} namespace - Namespace name
 * @returns {boolean}
 */
export function isNamespaceCached (lang, namespace) {
  return cache.has(`${lang}:${namespace}`)
}

/**
 * Check if all namespaces are cached for a language
 *
 * @param {string} lang - Language code
 * @param {string[]} namespaces - Namespaces to check
 * @returns {boolean}
 */
export function areNamespacesCached (lang, namespaces) {
  return namespaces.every(ns => isNamespaceCached(lang, ns))
}

/**
 * Get all cached namespaces for a language
 *
 * @param {string} lang - Language code
 * @returns {string[]} Array of cached namespace names
 */
export function getCachedNamespaces (lang) {
  const cached = []
  for (const key of cache.keys()) {
    if (key.startsWith(`${lang}:`)) {
      cached.push(key.split(':')[1])
    }
  }
  return cached
}

/**
 * Preload namespaces (fire-and-forget)
 * Useful for prefetching on hover or idle time
 *
 * @param {string} lang - Language code
 * @param {string[]} namespaces - Namespaces to preload
 */
export function preloadNamespaces (lang, namespaces) {
  ensureNamespaces(lang, namespaces).catch(error => {
    console.warn(`[i18n] Preload failed for ${lang}:`, error)
  })
}

/**
 * Clear cache
 *
 * @param {string} [lang] - Optional: clear only specific language
 */
export function clearCache (lang = null) {
  if (lang) {
    for (const key of cache.keys()) {
      if (key.startsWith(`${lang}:`)) {
        cache.delete(key)
      }
    }
  } else {
    cache.clear()
  }
}

/**
 * Get cache stats (useful for debugging)
 *
 * @returns {Object} Cache statistics
 */
export function getCacheStats () {
  const stats = {
    totalEntries: cache.size,
    byLanguage: {}
  }

  for (const key of cache.keys()) {
    const [lang] = key.split(':')
    stats.byLanguage[lang] = (stats.byLanguage[lang] || 0) + 1
  }

  return stats
}
